import DOMPurify from 'dompurify'
import { z } from 'zod'

/**
 * Input sanitization utilities for ProSci Portal
 */

/**
 * Sanitize HTML content while preserving safe formatting
 * Allows common formatting tags but removes script tags and event handlers
 */
export function sanitizeHTML(input: string): string {
  if (!input || typeof input !== 'string') {
    return ''
  }

  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote',
      'a'
    ],
    ALLOWED_ATTR: ['href', 'target'],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false
  })
}

/**
 * Sanitize plain text input by removing all HTML tags and potentially dangerous characters
 */
export function sanitizeText(input: string): string {
  if (!input || typeof input !== 'string') {
    return ''
  }

  // Remove all HTML tags and decode HTML entities
  const textOnly = DOMPurify.sanitize(input, { 
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  })

  // Trim whitespace and normalize line breaks
  return textOnly.trim().replace(/\s+/g, ' ')
}

/**
 * Sanitize email addresses
 */
export function sanitizeEmail(input: string): string {
  if (!input || typeof input !== 'string') {
    return ''
  }

  // Remove HTML tags and trim
  const cleaned = sanitizeText(input).toLowerCase()
  
  // Basic email pattern validation (additional validation should be done with Zod)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(cleaned) ? cleaned : ''
}

/**
 * Sanitize form data object recursively
 */
export function sanitizeFormData(data: Record<string, unknown>, htmlFields: string[] = []): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) {
      sanitized[key] = value
    } else if (typeof value === 'string') {
      if (htmlFields.includes(key)) {
        // Allow HTML for specified fields (like rich text content)
        sanitized[key] = sanitizeHTML(value)
      } else if (key.toLowerCase().includes('email')) {
        // Special handling for email fields
        sanitized[key] = sanitizeEmail(value)
      } else {
        // Default text sanitization
        sanitized[key] = sanitizeText(value)
      }
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeFormData(value, htmlFields)
    } else {
      // Keep other types as-is (numbers, booleans, arrays)
      sanitized[key] = value
    }
  }

  return sanitized
}

/**
 * Create a sanitizing transform for Zod schemas
 */
export function createSanitizeTransform(allowHTML: boolean = false) {
  return (value: string) => {
    if (typeof value !== 'string') return value
    return allowHTML ? sanitizeHTML(value) : sanitizeText(value)
  }
}

/**
 * Enhanced string schema with built-in sanitization
 */
export function sanitizedString(options: {
  maxLength?: number
  required?: boolean
  allowHTML?: boolean
  isEmail?: boolean
} = {}) {
  const { maxLength = 255, required = false, allowHTML = false, isEmail = false } = options
  
  let schema = z.string()
  
  if (isEmail) {
    schema = schema.transform(sanitizeEmail)
  } else {
    schema = schema.transform(createSanitizeTransform(allowHTML))
  }
  
  if (required) {
    schema = schema.min(1, { message: 'This field is required' })
  }
  
  if (isEmail) {
    schema = schema.email({ message: 'Please enter a valid email address' })
  }
  
  schema = schema.max(maxLength, { message: `Must be ${maxLength} characters or less` })
  
  return schema
}